import { useAuth } from '../providers'
import { useCallback, useMemo } from 'react'
import { getChatApiRoute } from './v1Client'

type ChatApiVersion = 'v1' | 'v2' | 'v3' | 'v4'

interface FetchOptions {
  host?: string
  version?: ChatApiVersion
  body?: FormData | string
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  bearerToken: string
  signal?: AbortSignal
  headers?: Record<string, string>
}

interface PublicFetchOptions {
  version?: ChatApiVersion
  body?: FormData | string
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH'
  signal?: AbortSignal
}

interface RequestOptions {
  host?: string
  version: ChatApiVersion
  signal?: AbortSignal
  headers?: Record<string, string>
}

const fetchRequest = async (
  route: string,
  { host, bearerToken, body, version, method, signal, headers }: FetchOptions,
) => {
  const url = host ? `${host}/api/${version ?? 'v3'}/${route}` : getChatApiRoute(`api/${version ?? 'v3'}/${route}`)
  return fetch(url, {
    method: method,
    body: body,
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      ...(headers ?? {}),
    },
    signal: signal,
  })
}

const fetchPublicRequest = async (route: string, { body, version, method, signal }: PublicFetchOptions) => {
  return fetch(getChatApiRoute(`api/${version ?? 'v3'}/${route}`), {
    method: method,
    body: body,
    signal: signal,
  })
}

type HandleUnauthorized = ((rejectedToken: string) => Promise<string | null>) | undefined

const fetchWithAuth = async (
  route: string,
  fetchOptions: Omit<FetchOptions, 'bearerToken'>,
  getAuthToken: () => Promise<string>,
  handleUnauthorized: HandleUnauthorized,
) => {
  const accessToken = await getAuthToken()
  const response = await fetchRequest(route, { ...fetchOptions, bearerToken: accessToken })
  if (response.status === 401 && handleUnauthorized) {
    const newToken = await handleUnauthorized(accessToken)
    if (newToken) {
      return fetchRequest(route, { ...fetchOptions, bearerToken: newToken })
    }
  }
  return response
}

export const useApi = () => {
  const { getAuthToken, handleUnauthorized } = useAuth()

  const get = useCallback(
    async (route: string, options?: Partial<RequestOptions>) => {
      return fetchWithAuth(
        route,
        { host: options?.host, method: 'GET', version: options?.version, signal: options?.signal },
        getAuthToken,
        handleUnauthorized,
      )
    },
    [getAuthToken, handleUnauthorized],
  )

  const post = useCallback(
    async (route: string, body?: FormData | string, options?: Partial<RequestOptions>) => {
      return fetchWithAuth(
        route,
        {
          host: options?.host,
          headers: options?.headers,
          body,
          method: 'POST',
          version: options?.version,
          signal: options?.signal,
        },
        getAuthToken,
        handleUnauthorized,
      )
    },
    [getAuthToken, handleUnauthorized],
  )

  const patch = useCallback(
    async (route: string, body?: FormData | string, options?: Partial<RequestOptions>) => {
      return fetchWithAuth(
        route,
        {
          host: options?.host,
          headers: options?.headers,
          body,
          method: 'PATCH',
          version: options?.version,
          signal: options?.signal,
        },
        getAuthToken,
        handleUnauthorized,
      )
    },
    [getAuthToken, handleUnauthorized],
  )

  const deleteRequest = useCallback(
    async (route: string, options?: Partial<RequestOptions>) => {
      return fetchWithAuth(
        route,
        { host: options?.host, method: 'DELETE', version: options?.version, signal: options?.signal },
        getAuthToken,
        handleUnauthorized,
      )
    },
    [getAuthToken, handleUnauthorized],
  )

  const getImage = useCallback(
    async (imageUrl: string, options?: Partial<RequestOptions>) => {
      const formData = new FormData()
      formData.append('imageUrl', imageUrl)

      const response = await fetchWithAuth(
        'image/',
        { method: 'POST', body: formData, version: options?.version, signal: options?.signal },
        getAuthToken,
        handleUnauthorized,
      )

      if (!response.ok) {
        throw new Error('Failed to fetch image')
      }

      const blob = await response.blob()
      return URL.createObjectURL(blob)
    },
    [getAuthToken, handleUnauthorized],
  )

  const getSharedImage = useCallback(async (imageUrl: string, options?: Partial<RequestOptions>) => {
    const formData = new FormData()
    formData.append('imageUrl', imageUrl)

    const response = await fetchPublicRequest('image/public', {
      method: 'POST',
      body: formData,
      version: options?.version,
      signal: options?.signal,
    })

    if (!response.ok) {
      throw new Error('Failed to fetch shared image')
    }

    const blob = await response.blob()
    return URL.createObjectURL(blob)
  }, [])

  const getImageByFileId = useCallback(
    async (fileId: string, conversationId: string, options?: Partial<RequestOptions>) => {
      const response = await fetchWithAuth(
        `file/${conversationId}/${fileId}`,
        { method: 'GET', version: options?.version, signal: options?.signal },
        getAuthToken,
        handleUnauthorized,
      )

      if (!response.ok) {
        throw new Error('Failed to fetch image')
      }

      const data = await response.blob()
      return URL.createObjectURL(data)
    },
    [getAuthToken, handleUnauthorized],
  )

  return useMemo(() => {
    return {
      get,
      post,
      patch,
      deleteRequest: deleteRequest,
      getImage,
      getSharedImage,
      getImageByFileId,
    }
  }, [get, post, patch, deleteRequest, getImage, getSharedImage, getImageByFileId])
}
